import { Frequency } from 'components/Frequency'
import { RepeatType } from 'components/Frequency/constants'
import { validateFrequencyData } from 'components/Frequency/logic'
import { TagMatch } from 'components/TagMatchToggle'
import { TagScopePicker } from 'components/TagScopePicker'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getBalloonNoticeConfig,
  getBalloonNoticeSchedule,
  runBalloonNoticeScheduleNow,
  saveBalloonNoticeSchedule,
} from 'services'
import { Button, Checkbox, Modal } from 'stories/components'

interface Level {
  key: string
  label: string
  description: string
  minDays: number
  defaultTemplate: string
}

// Configure a recurring Balloon Payment Notices run. Persists the template map + delivery so
// scheduled runs go out automatically without the generate modal.
export default function BalloonNoticesScheduleModal({
  onClose,
  allTags,
}: {
  onClose: () => void
  allTags: string[]
}) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [errorCheck, setErrorCheck] = useState(false)

  const [levels, setLevels] = useState<Level[]>([])
  const [templates, setTemplates] = useState<string[]>([])
  const [docgenAvailable, setDocgenAvailable] = useState(true)

  const [frequency, setFrequency] = useState<Record<string, any>>({ repeatType: RepeatType.Repeat })
  const [enabled, setEnabled] = useState(true)
  const [templateMap, setTemplateMap] = useState<Record<string, string>>({})
  const [tags, setTags] = useState<string[]>([])
  const [tagMatch, setTagMatch] = useState<TagMatch>('any')
  const [excludeTags, setExcludeTags] = useState<string[]>([])
  const [attach, setAttach] = useState(true)
  const [email, setEmail] = useState(false)

  useEffect(() => {
    Promise.all([getBalloonNoticeConfig().catch(() => null), getBalloonNoticeSchedule().catch(() => null)])
      .then(([cfg, sched]: any[]) => {
        const lv = cfg?.levels || []
        setLevels(lv)
        setTemplates(cfg?.templates || [])
        setDocgenAvailable(cfg?.docgenAvailable !== false)
        // Seed from an existing schedule, else from config defaults.
        if (sched?.id) {
          setFrequency(sched.frequency || {})
          setEnabled(sched.enabled ?? true)
          setTemplateMap(sched.templateMap || {})
          setTags(Array.isArray(sched.tags) ? sched.tags : [])
          setExcludeTags(Array.isArray(sched.excludeTags) ? sched.excludeTags : [])
          setTagMatch(sched.tagMatch === 'all' ? 'all' : 'any')
          setAttach(sched.attach ?? true)
          setEmail(!!sched.email)
        } else {
          setTemplateMap(Object.fromEntries(lv.map((l: Level) => [l.key, l.defaultTemplate || ''])))
        }
      })
      .finally(() => setLoading(false))
  }, [])


  const payload = () => ({
    frequency: { ...frequency, repeatType: enabled ? RepeatType.Repeat : RepeatType.NoRepeat },
    enabled,
    templateMap,
    tags,
    tagMatch,
    excludeTags,
    attach,
    email,
  })

  const onSave = async () => {
    setErrorCheck(true)
    if (enabled && validateFrequencyData(frequency, [], true)) {
      toast('Please complete the schedule.', { type: 'error' })
      return
    }
    if (enabled && !levels.some((l) => templateMap[l.key])) {
      toast('Map at least one notice level to a template.', { type: 'error' })
      return
    }
    setSaving(true)
    try {
      await saveBalloonNoticeSchedule(payload())
      toast('Balloon notice schedule saved.', { type: 'success' })
      onClose()
    } catch {
      toast('Failed to save the schedule.', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const onRunNow = async () => {
    setRunning(true)
    try {
      // Save first so the run uses the current options.
      await saveBalloonNoticeSchedule(payload())
      const res: any = await runBalloonNoticeScheduleNow()
      toast(res?.jobId ? 'Balloon notice run started.' : res?.message || 'Run started.', {
        type: res?.success === 0 ? 'error' : 'success',
      })
      if (res?.success !== 0) onClose()
    } catch {
      toast('Failed to run balloon notices.', { type: 'error' })
    } finally {
      setRunning(false)
    }
  }

  return (
    <Modal title="Schedule Balloon Payment Notices" isOpen onOk={onSave} onClose={onClose} loading={saving || loading}>
      <div className="w-[560px] min-h-[420px]">
        <p className="mb-3 text-sm text-gray-500">
          On this schedule, every loan nearing (or past) its balloon/maturity date is auto-matched to the strongest
          notice it qualifies for and it is generated from the mapped docgen template.
        </p>
        {!docgenAvailable && (
          <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            The document generation service could not be reached, so no templates were loaded.
          </div>
        )}
        <div className="mb-3">
          <Checkbox id="bn-sched-enabled" title="Enabled" value={enabled} onChange={setEnabled} />
        </div>

        <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">Escalation → Template</div>
        <div className="overflow-hidden rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <tbody>
              {levels.map((l) => (
                <tr key={l.key} className="border-t border-slate-100 first:border-t-0">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-gray-800">{l.label}</div>
                    <div className="text-[11px] text-gray-400">{l.description}</div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      value={templateMap[l.key] || ''}
                      onChange={(e) => setTemplateMap((m) => ({ ...m, [l.key]: e.target.value }))}
                    >
                      <option value="">— None (skip this level) —</option>
                      {templates.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex flex-col gap-1">
          <Checkbox id="bn-sched-attach" title="Attach each notice to its loan" value={attach} onChange={setAttach} />
          <Checkbox id="bn-sched-email" title="Email borrowers their notice" value={email} onChange={setEmail} />
        </div>

        <TagScopePicker
          className="mt-3"
          allTags={allTags}
          tags={tags}
          onChangeTags={setTags}
          tagMatch={tagMatch}
          onChangeTagMatch={setTagMatch}
          excludeTags={excludeTags}
          onChangeExcludeTags={setExcludeTags}
          includeHint="(optional)"
        />

        <div className={`mt-4 ${enabled ? '' : 'pointer-events-none opacity-40'}`}>
          <Frequency
            data={frequency}
            onChange={(value: any) => setFrequency(value)}
            errorCheck={errorCheck}
            repeatTypeToggle={false}
            showTime={true}
          />
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-[12px] text-gray-400">
            Scheduled runs only fire in production. Use “Run now” to generate immediately.
          </span>
          <Button color="gray" outline onClick={onRunNow} loading={running} disabled={saving}>
            Run now
          </Button>
        </div>
      </div>
    </Modal>
  )
}
